import classNames from 'clsx'
import { arrayOf, bool, node, shape } from 'prop-types'
import { Component } from 'react'

import { IS_JEST, IS_STORYBOOK, TENANT_IT } from 'shared/consts'
import store from 'shared/store'
import { type InferPropsWithDefaults } from 'shared/utils/propTypes'
import { type Tenant } from 'types/shopConfig'
import type { SearchConfig } from 'types/shopConfig'
import {
  convertHitToHistorySuggestion,
  convertHitToSuggestion,
  getTopProductsAndSearches,
} from 'views/components/organisms/PreQuerySuggestions/helpers'
import LoadingWrapper from 'views/components/organisms/PreQuerySuggestions/LoadingWrapper'
import TopProducts from 'views/components/organisms/PreQuerySuggestions/TopProducts'
import TopSearches from 'views/components/organisms/PreQuerySuggestions/TopSearches'
import type {
  SearchHistorySuggestion,
  SearchHit,
  SearchSuggestion,
} from 'views/components/organisms/PreQuerySuggestions/types'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import { publicRuntimeConfigType } from 'views/utils/propTypes'

import PopularCategoriesOrBrands from './PopularCategoriesOrBrands'

class PreQuerySuggestionsExtendedNewConfig extends Component<
  ComponentProps,
  ComponentState
> {
  state: ComponentState = this.getInitialState()

  constructor(props: ComponentProps) {
    super(props)

    if (IS_JEST || IS_STORYBOOK) {
      // Don't disturb Chromatic
      return
    }
  }

  getInitialState(): ComponentState {
    const { topProducts, topSearches } = this.props

    // Top searches & products can be passed through the props:
    // it is used only for the Storybook stories
    return {
      topSearches: Array.isArray(topSearches) ? topSearches : [],
      topProducts: Array.isArray(topProducts) ? topProducts : [],
    } as unknown as ComponentState // TS linter can't comprehend it is safe
  }

  componentDidMount() {
    if (IS_JEST || IS_STORYBOOK) {
      return
    }

    const {
      publicRuntimeConfig: {
        publicConfig: { cdnUrl, searchConfig },
        pageProperties: { expa },
      },
      mobile,
    } = this.props

    const {
      publicConfig: { tenant },
    } = store.getPublicRuntimeConfig()

    const deviceClass = mobile ? 'mobile' : 'desktop'

    void getTopProductsAndSearches(
      {
        deviceClass,
        expa,
        searchConfig: searchConfig as SearchConfig,
      },
      ({ results }) => {
        const [topProductsResutls, topSearchesResults] = results
        const topProducts = topProductsResutls?.hits.map(product =>
          convertHitToSuggestion({
            product: product as SearchHit,
            cdnUrl,
            queryID: topProductsResutls?.queryID,
            isRebrandingEnabled: tenant === TENANT_IT,
          })
        )
        const topSearches = topSearchesResults?.hits.map(product =>
          convertHitToHistorySuggestion({
            product: product as SearchHit,
            queryID: topSearchesResults.queryID,
          })
        )

        this.setState({ topSearches })
        this.setState({ topProducts })
      }
    )
  }

  render() {
    const {
      hasSearchHistory,
      mobile,
      children,
      topCategories,
      topBrands,
      publicRuntimeConfig: {
        publicConfig: { language, tenant },
      },
    } = this.props
    const { topSearches, topProducts } = this.state
    const hasTopProducts = Boolean(topProducts?.length)
    const hasTopSearches = Boolean(topSearches?.length)

    if (!topSearches?.length) {
      return <LoadingWrapper mobile={mobile} />
    }

    return (
      <div
        className={classNames('o-PreQuerySuggestions__results', {
          'o-SearchBox__results--mobile': mobile,
          'o-SearchBox__results u-padding-x-small': !mobile,
        })}
      >
        <div className="l-flex l-flex--start">
          <div className="u-padding--right@medium u-padding-small--right">
            {hasSearchHistory && children}
          </div>
          <div className="u-padding--right@medium u-padding-small--right">
            {hasTopSearches && <TopSearches hits={topSearches} />}
          </div>
          {topCategories && (
            <PopularCategoriesOrBrands
              tenant={tenant as Tenant}
              language={language}
              mode="categories"
            />
          )}
          {topBrands && (
            <PopularCategoriesOrBrands
              tenant={tenant as Tenant}
              language={language}
              mode="brands"
            />
          )}
        </div>
        {hasTopProducts && (
          <TopProducts hits={topProducts} mobile={Boolean(mobile)} />
        )}
      </div>
    )
  }
}

const propTypes = {
  publicRuntimeConfig: shape(publicRuntimeConfigType).isRequired,
  hasSearchHistory: bool,
  children: node,
  mobile: bool,
  topSearches: arrayOf(shape({})),
  topProducts: arrayOf(shape({})),
  topBrands: bool,
  topCategories: bool,
}

const defaultProps = {
  hasSearchHistory: false,
  children: null,
  mobile: false,
  topSearches: null,
  topProducts: null,
  topBrands: false,
  topCategories: false,
}

type ComponentProps = InferPropsWithDefaults<
  typeof propTypes,
  typeof defaultProps
>

type ComponentState = {
  topSearches: SearchHistorySuggestion[]
  topProducts: SearchSuggestion[]
}

export default withShopAndRuntimeConfig(PreQuerySuggestionsExtendedNewConfig)
